import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatVersion, resolveVersion, type AppVersion as Version } from 'shared'
import { api } from '../utils/api'
import { cx } from '../lib/utils'

// Set by CI; a local build has neither, and says so rather than inventing one.
const frontend = resolveVersion(import.meta.env.VITE_BUILD_NUMBER, import.meta.env.VITE_COMMIT_SHA)

// One request per page load, shared by every footer that mounts. A failure is
// not kept: the next footer to mount asks again.
let backendBuild: Promise<string | null> | undefined
function loadBackendBuild(): Promise<string | null> {
  backendBuild ??= (async () => {
    try {
      const body = await api.get<{ version?: Pick<Version, 'build'> }>('/health')
      return body.version?.build ?? null
    } catch {
      backendBuild = undefined
      return null
    }
  })()
  return backendBuild
}

/**
 * The app's version in small type: `1.142 · a3f9c21`. When the API answers
 * with a different build the number is flagged, because the usual cause is a
 * stale cached frontend or a deploy that only half finished.
 */
export default function AppVersion({ className }: { className?: string }) {
  const { t } = useTranslation()
  const [build, setBuild] = useState<string | null>(null)

  useEffect(() => {
    void loadBackendBuild().then(setBuild)
  }, [])

  // A local build has no number to compare against, so it never flags one.
  const mismatch = build !== null && build !== frontend.build
  const message = mismatch
    ? t('version.mismatch', { app: frontend.build, api: build })
    : undefined

  return (
    <p
      title={message}
      className={cx(
        'text-xs tabular-nums text-gray-400 dark:text-gray-500',
        mismatch && 'text-amber-600 dark:text-amber-400',
        className,
      )}
    >
      <span className="sr-only">{t('version.label')}: </span>
      {formatVersion(frontend)}
      {mismatch && (
        <>
          <span aria-hidden="true"> ⚠</span>
          <span className="sr-only"> ({message})</span>
        </>
      )}
    </p>
  )
}
